// deps
import { useCallback, useContext, useMemo } from "react";

//analytics
import {
    onClickCancelDelink,
    onClickConfirmDelink,
    onClickCrossDelinkFailure,
    onClickDelinkCta,
    onClickTryAgainDelink,
    onImpressionConfirmDelink,
    onImpressionCurrentBalance,
    onImpressionFaliedDelink,
    onImpressionSuccessDelink,
    onScreenViewAddMoney,
} from "@payment-client/analytics/events/AddMoneyDelinkWallet";
import { SCREEN_NAME } from "@payment-client/analytics/constants";
import { onAddMoneyImpressionEvent } from "@payment-client/analytics/events/Payment";

//internal-deps
import { AnalyticsContext } from "@swiggy-private/checkout-core";
import { WALLET_CODES } from "@swiggy-private/payment-types";

interface UseWalletAnalyticsHandlerProps {
    walletCode: WALLET_CODES;
    isAddMoneyToWalletEnabled: boolean;
}

interface DelinkWalletAnalyticHandler {
    onClickTryAgainDelink: VoidFunction;
    onClickCrossDelinkFailure: VoidFunction;
    onClickCancelDelink: VoidFunction;
    onClickConfirmDelink: VoidFunction;
    onImpressionConfirmDelink: VoidFunction;
    onClickDelinkCta: VoidFunction;
    onImpressionFaliedDelink: VoidFunction;
    onImpressionSuccessDelink: VoidFunction;
}

interface UseWalletAnalyticsHandlerReturn {
    onImpressionCurrentBalance: (walletBalance: number) => void;
    onAddMoneyImpressionEvent: (walletBalance: number) => void;
    onScreenViewAddMoney: (walletCode: WALLET_CODES) => void;
    delinkWalletAnalyticHandler: DelinkWalletAnalyticHandler;
}

const useWalletAnalyticsHandler = ({
    walletCode,
    isAddMoneyToWalletEnabled,
}: UseWalletAnalyticsHandlerProps): UseWalletAnalyticsHandlerReturn => {
    const { clickEvent, impressionEvent, screenViewEvent } = useContext(AnalyticsContext);

    const _onImpressionFaliedDelink = useCallback(() => {
        onImpressionFaliedDelink(walletCode, impressionEvent);
    }, [impressionEvent, walletCode]);

    const _onImpressionSuccessDelink = useCallback(() => {
        onImpressionSuccessDelink(walletCode, impressionEvent);
    }, [impressionEvent, walletCode]);

    const _onImpressionCurrentBalance = useCallback(
        (walletBalance: number) => {
            const screenName = isAddMoneyToWalletEnabled ? SCREEN_NAME.ADD_MONEY : SCREEN_NAME.WALLET_VIEW;
            onImpressionCurrentBalance(screenName, walletCode, walletBalance, impressionEvent);
        },
        [impressionEvent, isAddMoneyToWalletEnabled, walletCode],
    );

    const _onAddMoneyImpressionEvent = useCallback(
        (walletBalance: number) => {
            onAddMoneyImpressionEvent(walletBalance, walletCode, impressionEvent, SCREEN_NAME.ADD_MONEY);
        },
        [impressionEvent, walletCode],
    );

    const _onScreenViewAddMoney = useCallback(
        (walletCode: WALLET_CODES) => {
            onScreenViewAddMoney(walletCode, screenViewEvent);
        },
        [screenViewEvent],
    );

    const _onClickTryAgainDelink = useCallback(() => {
        onClickTryAgainDelink(walletCode, clickEvent);
    }, [clickEvent, walletCode]);

    const _onClickCrossDelinkFailure = useCallback(() => {
        onClickCrossDelinkFailure(walletCode, clickEvent);
    }, [clickEvent, walletCode]);

    const _onClickCancelDelink = useCallback(() => {
        onClickCancelDelink(walletCode, clickEvent);
    }, [clickEvent, walletCode]);

    const _onClickConfirmDelink = useCallback(() => {
        onClickConfirmDelink(walletCode, clickEvent);
    }, [clickEvent, walletCode]);

    const _onImpressionConfirmDelink = useCallback(() => {
        onImpressionConfirmDelink(walletCode, impressionEvent);
    }, [impressionEvent, walletCode]);

    const _onClickDelinkCta = useCallback(() => {
        const screenName = isAddMoneyToWalletEnabled ? SCREEN_NAME.ADD_MONEY : SCREEN_NAME.WALLET_VIEW;
        onClickDelinkCta(screenName, walletCode, clickEvent);
    }, [clickEvent, isAddMoneyToWalletEnabled, walletCode]);

    const delinkWalletAnalyticHandler = useMemo(
        () => ({
            onClickTryAgainDelink: _onClickTryAgainDelink,
            onClickCrossDelinkFailure: _onClickCrossDelinkFailure,
            onClickCancelDelink: _onClickCancelDelink,
            onClickConfirmDelink: _onClickConfirmDelink,
            onImpressionConfirmDelink: _onImpressionConfirmDelink,
            onClickDelinkCta: _onClickDelinkCta,
            onImpressionFaliedDelink: _onImpressionFaliedDelink,
            onImpressionSuccessDelink: _onImpressionSuccessDelink,
        }),
        [
            _onClickCancelDelink,
            _onClickConfirmDelink,
            _onClickCrossDelinkFailure,
            _onClickDelinkCta,
            _onClickTryAgainDelink,
            _onImpressionConfirmDelink,
            _onImpressionFaliedDelink,
            _onImpressionSuccessDelink,
        ],
    );

    return {
        onImpressionCurrentBalance: _onImpressionCurrentBalance,
        onAddMoneyImpressionEvent: _onAddMoneyImpressionEvent,
        onScreenViewAddMoney: _onScreenViewAddMoney,
        delinkWalletAnalyticHandler,
    };
};

export { useWalletAnalyticsHandler };
